import { Delta, EditorRange } from '@typewriter/editor';
import { getNow } from './datetime';
import { uuid } from '../libs/uuid';
import { writable, Readable, derived, observe } from '../libs/store';
import { SprintEditor } from '../editor';
import { getDb, SprintDatabase } from './db';
import { saveSprint, getSprint, deleteSprint as deleteRemoteSprint, saveWordcount } from './server';
import { user } from './user';
import { analytics } from './firebase';

export const DATA_EXPIRES = 1000 * 60 * 60 * 24;
export const SPRINT_OPENS = 1000 * 60 * 15;
export const saved = writable(true);
export const editor = writable<SprintEditor>(null);
export const storedSprints = createStoredSprintsStore();
export const sprint = writable<Sprint>(null);
export const filters = createFiltersStore();

// Auto-submit wordcount
let lastSprint;
observe([ sprint, user ], ([ sprint, user ]) => {
  if (!sprint || !user) return;
  if (!lastSprint) return lastSprint = sprint;
  if (sprint.id === lastSprint.id && sprint.wordcount === lastSprint.wordcount) return lastSprint = sprint;
  lastSprint = sprint;
  if (sprint.wordcount !== undefined) {
    saveWordcount(sprint.id, user.uid, sprint.wordcount);
  }
});

const remoteSprintProps = new Set([ 'id', 'title', 'prompt', 'creator', 'start', 'end', 'listed' ]);


export enum Status {
  Pending = 'pending',
  Open = 'open',
  Started = 'started',
  Finished = 'finished',
}

export const StatusText = {
  [Status.Pending]: 'Coming soon',
  [Status.Open]: 'Open',
  [Status.Started]: 'In progress',
  [Status.Finished]: 'Finished',
}

export interface SprintFilters {
  finished: boolean;
  personal: boolean;
  group: boolean;
}

export interface RemoteSprint {
  id?: string;
  title?: string;
  prompt?: Delta;
  creator?: string;
  start: number;
  end: number;
  listed?: boolean;
}

export interface Sprint extends RemoteSprint {
  totalWordcount?: number; // Word count that existed before the sprint started
  wordcount?: number;
  content?: Delta;
  selection?: EditorRange;
}

export interface SprintCreateOptions {
  start: number;
  end: number;
  creator?: string;
  title?: string;
  prompt?: Delta;
  listed?: boolean;
}

export interface StoreSprintsStore extends Readable<Sprint[]> {
  loaded: Readable<boolean>;
  save(sprint: Sprint): Promise<Sprint>;
  getSprint(id: string): Sprint;
}

interface SprintTiming {
  start: number;
  end: number;
  duration: number;
}

interface SprintMember {
  uid: string;
  name: string;
  count: number;
}

export interface FetchOptions {
  authed?: boolean;
  body?: any;
  method?: string;
}


export async function createSprint(options: SprintCreateOptions): Promise<Sprint> {
  let sprint: Sprint;
  const duration = (options.end - options.start) / 1000 / 60;
  if (options.creator) {
    sprint = await saveSprint(options);
    analytics.logEvent('create_sprint', { type: 'group', listed: options.listed, duration });
  } else {
    const id = uuid(6);
    sprint = { ...options, id, wordcount: 0, totalWordcount: 0 };
    analytics.logEvent('create_sprint', { type: 'personal', duration });
  }
  return await storedSprints.save(sprint);
}


export async function updateSprint(sprint: Sprint): Promise<Sprint> {
  const remoteSprint = { ...sprint };
  Object.keys(remoteSprint).forEach(key => {
    if (!remoteSprintProps.has(key)) delete remoteSprint[key];
  });
  await saveSprint(remoteSprint);
  analytics.logEvent('update_sprint');
  return await storedSprints.save(sprint);
}


export async function deleteSprint(sprintId: string): Promise<void> {
  const db = await getDb();
  await db.sprints.delete(sprintId);
  analytics.logEvent('delete_sprint');
  if (sprintId.length === 12) await deleteRemoteSprint(sprintId);
}


export async function loadSprint(sprintId: string) {
  const db = await getDb();
  let data = await db.sprints.get(sprintId);

  if (sprintId.length === 12) {
    // Load from server
    const remote = await getSprint(sprintId);
    if (remote && data) data = mergeSprints(remote, data);
    else if (remote) data = remote;
  }

  sprint.set(data);
}

export function unloadSprint() {
  sprint.set(null);
}

export function getStatus(sprint: Sprint) {
  const now = getNow();
  if (!sprint) return Status.Pending;
  if (sprint.end <= now) return Status.Finished;
  if (sprint.start <= now) return Status.Started;
  if (sprint.start - SPRINT_OPENS <= now) return Status.Open;
  return Status.Pending;
}


export const sprintTiming = derived(sprint, sprint => {
  const start = sprint && sprint.start || 0;
  const end = sprint && sprint.end || 0;
  return { start, end, duration: end - start } as SprintTiming;
});
export const timeLeft = createTimeLeftStore(sprintTiming);
export const percent = derived([ timeLeft, sprintTiming ], ([ left, { duration } ]) => {
  return (1 - Math.min(duration, Math.max(0, left)) / duration);
});
export const secondsLeft = derived(timeLeft, left => Math.ceil(left / 1000));
export const secondsUntil = derived([ secondsLeft, sprintTiming ], ([ left, { duration }]) => Math.max(0, left - duration / 1000));
export const status = derived([ secondsLeft, secondsUntil ], ([ left, until ]) => left <= 0 ? Status.Finished : !until ? Status.Started : Status.Pending);


function createTimeLeftStore(timing: Readable<SprintTiming>): Readable<number> {
  return writable(0, (set) => onAnimationFrame(() => {
    set(Math.max(0, timing.get().end - getNow()));
  }));
}


function onAnimationFrame(method: Function, ...args: any[]) {
  method(...args);
  let interval = requestAnimationFrame(function onFrame() {
    method(...args);
    interval = requestAnimationFrame(onFrame);
  })
  return () => cancelAnimationFrame(interval);
}


function createStoredSprintsStore(): StoreSprintsStore {
  let db: SprintDatabase;

  const { set, get, subscribe } = writable<Sprint[]>([]);
  const loaded = writable(false);

  load();

  async function load() {
    db = await getDb();
    const sprints = await db.sprints.getAll();
    db.sprints.on('change', onChange);
    set(sprints);
    loaded.set(true);
  }

  function onChange(record: Sprint, key: string, from: 'local' | 'remote') {
    let sprints = get().filter(sprint => sprint.id !== key);
    if (record) sprints = sprints.concat(record).sort(sprintSort);
    set(sprints);
  }

  async function save(sprint: Sprint) {
    await db.sprints.put(sprint);
    return sprint;
  }

  function getSprint(id: string) {
    return get().find(sprint => sprint.id === id);
  }

  return { loaded, get, subscribe, save, getSprint };
}

function createFiltersStore() {
  let initial = {
    finished: true,
    personal: true,
    group: true,
  };

  if (localStorage.filters) {
    try {
      initial = JSON.parse(localStorage.filters);
    } catch (e) {}
  }

  const { get, set, subscribe } = writable(initial);

  function toggle(filter: 'finished' | 'personal' | 'group') {
    let filters = get();
    filters = { ...filters, [filter]: !filters[filter] };
    localStorage.filters = JSON.stringify(filters);
    set(filters);
  }

  return {
    toggle,
    get, set, subscribe
  };
}


export function sprintSort(a: Sprint, b: Sprint) {
  return a.start - b.start;
}

export function sprintFilter(sprints: Sprint[], filters: SprintFilters) {
	const expires = getNow() - DATA_EXPIRES;
  const hasExpired = sprints.length && sprints[sprints.length - 1].end < expires;
  const hasUserFilter = filters && (!filters.finished || !filters.personal || !filters.group);
	if (!hasExpired && !hasUserFilter) return sprints;

	return sprints.filter(sprint => {
    if (sprint.end < expires) return false;
		if (!filters.finished && getStatus(sprint) === Status.Finished) return false;
		if (!filters.personal && sprint.id.length !== 12) return false;
		if (!filters.group && sprint.id.length === 12) return false;
		return true;
	});
}


function mergeSprints(remote, local) {
  Object.keys(local).forEach(key => {
    if (!remote.hasOwnProperty(key)) remote[key] = local[key];
  });
  return remote;
}
